import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useOptionalSession } from '@client/lib/useSession';
import { apiGet, apiPost, apiPatch, apiDelete, ApiClientError } from '@client/lib/api';
import { formatCents, parseCents } from '@shared/money';
import { type InvoiceStatus } from '@shared/constants';
import { updateInvoiceSchema } from '@shared/schemas/invoice';
import styles from './InvoiceDetailPage.module.css';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
}

interface Payment {
  id: string;
  amountCents: number;
  paymentMethod: string;
  paidAt: string;
  voidedAt: string | null;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customer: { id: string; name: string } | null;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  items: LineItem[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  amountPaidCents: number;
  balanceDueCents: number;
  payments: Payment[];
  customerMessage: string | null;
  internalNotes: string | null;
}

type EditableInvoice = {
  issueDate: string;
  dueDate: string;
  customerMessage: string;
  internalNotes: string;
  items: { description: string; quantity: string; unitPrice: string }[];
};

function normalizeNullable(value: string | null | undefined): string {
  return value ?? '';
}

function itemsEqual(
  a: { description: string; quantity: string; unitPrice: string }[],
  b: { description: string; quantity: string; unitPrice: string }[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (!x || !y) return false;
    if (x.description !== y.description) return false;
    if (x.quantity !== y.quantity) return false;
    if (x.unitPrice !== y.unitPrice) return false;
  }
  return true;
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  // The organization, for the share message. Never hardcode the business name.
  const organization = useOptionalSession()?.organization ?? null;
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<boolean>(false);
  const [editValues, setEditValues] = useState<EditableInvoice>({
    issueDate: '',
    dueDate: '',
    customerMessage: '',
    internalNotes: '',
    items: [],
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const saveRef = useRef<boolean>(false);
  const [paymentInput, setPaymentInput] = useState<string>('');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [recording, setRecording] = useState<boolean>(false);
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const [shareData, setShareData] = useState<{ url: string; expiresAt: string } | null>(null);
  const [sharing, setSharing] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<Invoice>(`/api/v1/invoices/${id}`);
      setInvoice(data);
    } catch (e) {
      if (e instanceof ApiClientError) {
        if (e.status === 404) {
          setError('Invoice not found');
        } else {
          setError(e.message);
        }
      } else {
        setError('Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <div className={styles.loading}>Loading invoice…</div>;
  }

  if (error) {
    if (error === 'Invoice not found') {
      return <div className={styles.notFound}>This invoice could not be found.</div>;
    }
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button type="button" className={styles.retryButton} onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const isSettled = invoice.status === 'PAID' || invoice.status === 'VOID';

  const startEdit = () => {
    setEditValues({
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate,
      customerMessage: normalizeNullable(invoice.customerMessage),
      internalNotes: normalizeNullable(invoice.internalNotes),
      items: invoice.items.map((it) => ({
        description: it.description,
        quantity: String(it.quantity),
        unitPrice: formatCents(it.unitPriceCents),
      })),
    });
    setEditError(null);
    setEditing(true);
  };

  const discard = () => {
    setEditing(false);
    setEditError(null);
  };

  const updateItem = (
    index: number,
    field: 'description' | 'quantity' | 'unitPrice',
    value: string
  ) => {
    setEditValues((prev) => {
      const items = [...prev.items];
      const item = items[index];
      if (!item) return prev;
      items[index] = { ...item, [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setEditValues((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: '', unitPrice: '' }],
    }));
  };

  const removeItem = (index: number) => {
    setEditValues((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const buildPatch = (): Record<string, unknown> | null => {
    const patch: Record<string, unknown> = {};

    if (editValues.issueDate !== invoice.issueDate) {
      patch.issueDate = editValues.issueDate;
    }
    if (editValues.dueDate !== invoice.dueDate) {
      patch.dueDate = editValues.dueDate;
    }

    const origMessage = normalizeNullable(invoice.customerMessage);
    if (editValues.customerMessage !== origMessage) {
      patch.customerMessage = editValues.customerMessage;
    }

    const origNotes = normalizeNullable(invoice.internalNotes);
    if (editValues.internalNotes !== origNotes) {
      patch.internalNotes = editValues.internalNotes;
    }

    const origItems = invoice.items.map((it) => ({
      description: it.description,
      quantity: String(it.quantity),
      unitPrice: formatCents(it.unitPriceCents),
    }));

    if (!itemsEqual(editValues.items, origItems)) {
      patch.items = editValues.items.map((it) => ({
        description: it.description,
        quantity: Number(it.quantity),
        unitPriceCents: parseCents(it.unitPrice),
      }));
    }

    if (Object.keys(patch).length === 0) {
      return null;
    }
    return patch;
  };

  const save = async () => {
    if (saving) return;
    if (saveRef.current) return;
    saveRef.current = true;
    setSaving(true);
    setEditError(null);

    const patch = buildPatch();
    if (patch === null) {
      setEditing(false);
      setSaving(false);
      saveRef.current = false;
      return;
    }

    try {
      const result = updateInvoiceSchema.safeParse(patch);
      if (!result.success) {
        setEditError(result.error.errors[0]?.message ?? 'Invalid input');
        setSaving(false);
        saveRef.current = false;
        return;
      }

      // The SERVER'S response is the new state, not a merge of what we sent.
      // Totals are recomputed server-side from the line items, so rebuilding
      // local state from the request body leaves a stale total on screen after
      // a line is edited -- the one number on this page nobody should have to
      // refresh to trust.
      const updated = await apiPatch<Invoice>(`/api/v1/invoices/${invoice.id}`, result.data);
      setInvoice(updated);
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiClientError) {
        setEditError(e.message);
      } else {
        setEditError('Something went wrong');
      }
    } finally {
      setSaving(false);
      saveRef.current = false;
    }
  };

  const recordPayment = async () => {
    if (recording) return;
    setRecording(true);
    setPaymentError(null);
    try {
      const cents = parseCents(paymentInput);
      if (cents === null) {
        setPaymentError('Enter a valid amount');
        setRecording(false);
        return;
      }
      await apiPost(`/api/v1/invoices/${invoice.id}/payments`, {
        amountCents: cents,
        paymentMethod: 'CASH',
      });
      setPaymentInput('');
      void load();
    } catch (e) {
      if (e instanceof ApiClientError) {
        setPaymentError(e.message);
      } else {
        setPaymentError('Something went wrong');
      }
    } finally {
      setRecording(false);
    }
  };

  const openShareDialog = () => {
    setCopied(false);
    setShareOpen(true);
    if (!shareData) {
      setSharing(true);
      apiPost<{ url: string; expiresAt: string }>(`/api/v1/invoices/${invoice.id}/share`)
        .then((data) => {
          setShareData(data);
        })
        .catch(() => {
          setShareData(null);
        })
        .finally(() => {
          setSharing(false);
        });
    }
  };

  const closeShareDialog = () => {
    setShareOpen(false);
    setCopied(false);
  };

  const copyShareLink = () => {
    if (!shareData) return;
    navigator.clipboard
      .writeText(shareData.url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard write failed — the user will have to select the URL manually.
      });
  };

  const businessNameForSubject = () => organization?.name ?? 'your service provider';

  const buildShareMessage = () => {
    if (!invoice || !shareData) return '';
    // The REAL business name, from the session. Hardcoding it here shipped a
    // string matching NEITHER live organization straight into customer texts.
    const businessName = organization?.name ?? 'your service provider';
    return `Check out your invoice from ${businessName}: ${shareData.url}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Invoice {invoice.invoiceNumber}</h1>
        <p>Status: {invoice.status}</p>
      </div>

      {!isSettled && !editing && (
        <button type="button" className={styles.recordButton} onClick={startEdit}>
          Edit invoice
        </button>
      )}

      {isSettled && !editing && (
        <p>Editing is not available for a {invoice.status.toLowerCase()} invoice.</p>
      )}

      {editing && (
        <div className={styles.section}>
          {editError && <p className={styles.paymentError}>{editError}</p>}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Issue date</label>
            <input
              type="date"
              value={editValues.issueDate}
              onChange={(e) => setEditValues({ ...editValues, issueDate: e.target.value })}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Due date</label>
            <input
              type="date"
              value={editValues.dueDate}
              onChange={(e) => setEditValues({ ...editValues, dueDate: e.target.value })}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Customer message</label>
            <textarea
              value={editValues.customerMessage}
              onChange={(e) => setEditValues({ ...editValues, customerMessage: e.target.value })}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Internal notes</label>
            <textarea
              value={editValues.internalNotes}
              onChange={(e) => setEditValues({ ...editValues, internalNotes: e.target.value })}
            />
          </div>

          <h2 className={styles.sectionTitle}>Line items</h2>
          {editValues.items.map((item, index) => (
            <div key={index} className={styles.lineItem}>
              <input
                type="text"
                placeholder="Description"
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
              />
              <input
                type="number"
                placeholder="Qty"
                value={item.quantity}
                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
              />
              <input
                type="text"
                placeholder="Price"
                value={item.unitPrice}
                onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
              />
              <button type="button" onClick={() => removeItem(index)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" onClick={addItem}>
            Add line
          </button>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.recordButton}
              disabled={saving}
              onClick={() => void save()}
              aria-label="Save"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={discard} disabled={saving}>
              Discard changes
            </button>
          </div>
        </div>
      )}

      {!editing && (
        <div className={styles.section}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Customer message</span>
            <span className={styles.fieldValue}>{invoice.customerMessage ?? ''}</span>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Internal notes</span>
            <span className={styles.fieldValue}>{invoice.internalNotes ?? ''}</span>
          </div>

          <ul className={styles.lineItems}>
            {invoice.items.map((item) => (
              <li key={item.id} className={styles.lineItem}>
                <span className={styles.lineItemDescription}>{item.description}</span>
                <span className={styles.lineItemPrice}>{formatCents(item.lineTotalCents)}</span>
              </li>
            ))}
          </ul>

          <div className={styles.totalsRow}>
            <span>Subtotal</span>
            <span>{formatCents(invoice.subtotalCents)}</span>
          </div>
          <div className={styles.totalsRow}>
            <span>Tax</span>
            <span>{formatCents(invoice.taxCents)}</span>
          </div>
          <div className={styles.totalsRow}>
            <span>Total</span>
            <span>{formatCents(invoice.totalCents)}</span>
          </div>
          <div className={styles.totalsRow}>
            <span>Amount paid</span>
            <span>{formatCents(invoice.amountPaidCents)}</span>
          </div>
          <div className={styles.totalsRow}>
            <span>Balance due</span>
            <span>{formatCents(invoice.balanceDueCents)}</span>
          </div>
        </div>
      )}

      {!editing && invoice.payments.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Payments</h2>
          <table className={styles.paymentsTable}>
            <thead>
              <tr>
                <th>Method</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Void</th>
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{payment.paymentMethod}</td>
                  <td>{payment.paidAt}</td>
                  <td>{formatCents(payment.amountCents)}</td>
                  <td>
                    {payment.voidedAt ? (
                      'Voided'
                    ) : (
                      <button
                        type="button"
                        className={styles.voidPaymentButton}
                        onClick={async () => {
                          if (window.confirm('Void this payment?')) {
                            await apiDelete(
                              `/api/v1/invoices/${invoice.id}/payments/${payment.id}`
                            );
                            void load();
                          }
                        }}
                      >
                        Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!editing && invoice.status !== 'VOID' && (
        <div className={styles.section}>
          <div className={styles.paymentForm}>
            <div className={styles.field}>
              <label className={styles.paymentLabel} htmlFor="payment-amount">
                Payment amount in dollars
              </label>
              <input
                id="payment-amount"
                type="text"
                className={styles.paymentInput}
                value={paymentInput}
                onChange={(e) => setPaymentInput(e.target.value)}
                placeholder="$0.00"
              />
            </div>
            <button
              type="button"
              className={styles.recordButton}
              disabled={recording}
              onClick={() => void recordPayment()}
            >
              {recording ? 'Recording…' : 'Record payment'}
            </button>
          </div>
          {paymentError && <p className={styles.paymentError}>{paymentError}</p>}
        </div>
      )}

      {!editing && invoice.status !== 'VOID' && (
        <div className={styles.section}>
          <button
            type="button"
            className={styles.voidInvoiceButton}
            onClick={async () => {
              if (window.confirm('Void this invoice? This cannot be undone.')) {
                await apiDelete(`/api/v1/invoices/${invoice.id}`);
                void load();
              }
            }}
          >
            Void invoice
          </button>
        </div>
      )}

      {!editing && (
        <div className={styles.section}>
          <button type="button" className={styles.shareButton} onClick={openShareDialog}>
            Share invoice
          </button>
        </div>
      )}

      {shareOpen && (
        <div className={styles.shareDialog}>
          <h2 className={styles.shareDialogTitle}>Share this invoice</h2>
          <p className={styles.shareDialogContent}>
            Share a link so your customer can view this invoice. Generating a new link invalidates
            the old one.
          </p>
          {sharing ? (
            <p>Generating link…</p>
          ) : shareData ? (
            <>
              <a
                className={styles.shareLink}
                href={shareData.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {shareData.url}
              </a>
              <div className={styles.shareActions}>
                <a
                  className={`${styles.shareAction} ${styles.shareActionSecondary}`}
                  href={`sms:?&body=${encodeURIComponent(buildShareMessage())}`}
                >
                  Send by text
                </a>
                <a
                  className={`${styles.shareAction} ${styles.shareActionSecondary}`}
                  href={`mailto:?subject=${encodeURIComponent(`Your invoice from ${businessNameForSubject()}`)}&body=${encodeURIComponent(buildShareMessage())}`}
                >
                  Send by email
                </a>
                <button
                  type="button"
                  className={`${styles.shareCopyButton}${copied ? ' ' + styles.copied : ''}`}
                  onClick={copyShareLink}
                >
                  {copied ? 'Copied!' : 'Copy link'}
                </button>
              </div>
            </>
          ) : (
            <p className={styles.paymentError}>Could not generate a share link.</p>
          )}
          <button type="button" className={styles.closeButton} onClick={closeShareDialog}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}
